import { useMemo } from "react";
import { PieChart, Pie, Cell } from "recharts";
import Icons from "@/src/components/Icons";
import { digitsEnToFa } from "@persian-tools/persian-tools";

interface TotalScoreProps {
  data: any[];
}

function TotalScoreItem({ data }: TotalScoreProps) {
  // Calculate totals and averages from all food items
  const totals = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        fiveStar: 0,
        fourStar: 0,
        threeStar: 0,
        twoStar: 0,
        oneStar: 0,
        totalRatings: 0,
        averageScore: "0.00",
        percentages: {
          fiveStar: 0,
          fourStar: 0,
          threeStar: 0,
          twoStar: 0,
          oneStar: 0,
        },
      };
    }

    // Sum up all ratings
    const fiveStar = data.reduce((sum, item) => sum + (item.fiveStar || 0), 0);
    const fourStar = data.reduce((sum, item) => sum + (item.fourStar || 0), 0);
    const threeStar = data.reduce(
      (sum, item) => sum + (item.threeStar || 0),
      0
    );
    const twoStar = data.reduce((sum, item) => sum + (item.twoStar || 0), 0);
    const oneStar = data.reduce((sum, item) => sum + (item.oneStar || 0), 0);

    const totalRatings = fiveStar + fourStar + threeStar + twoStar + oneStar;

    // Calculate weighted average
    const weightedSum =
      5 * fiveStar + 4 * fourStar + 3 * threeStar + 2 * twoStar + 1 * oneStar;

    const averageScore =
      totalRatings > 0 ? (weightedSum / totalRatings).toFixed(2) : "0.00";

    // Calculate percentages
    const calculatePercentage = (count: number) => {
      return totalRatings > 0 ? Math.round((count / totalRatings) * 100) : 0;
    };

    return {
      fiveStar,
      fourStar,
      threeStar,
      twoStar,
      oneStar,
      totalRatings,
      averageScore,
      percentages: {
        fiveStar: calculatePercentage(fiveStar),
        fourStar: calculatePercentage(fourStar),
        threeStar: calculatePercentage(threeStar),
        twoStar: calculatePercentage(twoStar),
        oneStar: calculatePercentage(oneStar),
      },
    };
  }, [data]);

  // Prepare chart data
  const chartData = useMemo(() => {
    return [
      {
        name: "۵ ستاره",
        value: totals.percentages.fiveStar,
        color: "#ff617d",
      },
      {
        name: "۴ ستاره",
        value: totals.percentages.fourStar,
        color: "#D84099",
      },
      {
        name: "۳ ستاره",
        value: totals.percentages.threeStar,
        color: "#b554c8",
      },
      {
        name: "۲ ستاره",
        value: totals.percentages.twoStar,
        color: "#6e68d6",
      },
      {
        name: "۱ ستاره",
        value: totals.percentages.oneStar,
        color: "#236ec9",
      },
    ];
  }, [totals]);

  // Prepare ratings data for progress bars
  const ratings = useMemo(() => {
    return [
      {
        value: 5,
        count: totals.fiveStar,
        percentage: totals.percentages.fiveStar,
        color: "#ff617d",
      },
      {
        value: 4,
        count: totals.fourStar,
        percentage: totals.percentages.fourStar,
        color: "#D84099",
      },
      {
        value: 3,
        count: totals.threeStar,
        percentage: totals.percentages.threeStar,
        color: "#b554c8",
      },
      {
        value: 2,
        count: totals.twoStar,
        percentage: totals.percentages.twoStar,
        color: "#6e68d6",
      },
      {
        value: 1,
        count: totals.oneStar,
        percentage: totals.percentages.oneStar,
        color: "#236ec9",
      },
    ];
  }, [totals]);

  return (
    <div className="shadow-md border border-gray-100-mmpz mt-4 rounded-xl min-h-[228px]">
      <div className="h-[66px] bg-gray-100-mmpz rounded-t-xl p-2 flex flex-col gap-2">
        <div className="flex w-full justify-between">
          <p className="text-base font-bold ">میانگین امتیاز:</p>
          <div className="flex items-center">
            <p className="text-sm font-medium ml-1">
              {digitsEnToFa(totals.averageScore)}
            </p>
            <Icons name="star" size={16} />
          </div>
        </div>
        <div className="flex w-full justify-between">
          <p className="text-sm">تعداد کل نظر های ثبت شده:</p>
          <p className="text-sm">
            {digitsEnToFa(totals.totalRatings)} <small>نفر</small>
          </p>
        </div>
      </div>
      <div className="p-4 flex flex-row">
        {/* Rating breakdown */}
        <div className="w-2/3 pl-4">
          {ratings.map((rating) => (
            <div
              key={rating.value}
              className="flex items-center mb-2 justify-end"
            >
              <div className="flex items-center ml-2">
                <span className="text-sm font-semibold ml-2">
                  {digitsEnToFa(rating.value)}
                </span>
                <Icons name="star" size={16} />
              </div>
              <div className="flex-grow mx-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${rating.percentage}%`,
                    backgroundColor: rating.color,
                  }}
                ></div>
              </div>
              <div className="text-right ml-2 w-16 text-xs">
                {digitsEnToFa(rating.count)} ({digitsEnToFa(rating.percentage)}
                %)
              </div>
            </div>
          ))}
        </div>

        {/* Recharts PieChart */}
        <div className="w-1/3 relative flex items-center justify-center">
          <PieChart width={120} height={120}>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={30}
              outerRadius={50}
              paddingAngle={0}
              dataKey="value"
              startAngle={90}
              endAngle={-270}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
          </PieChart>
        </div>
      </div>
    </div>
  );
}

export default TotalScoreItem;
